import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { Charge } from "../models/Charge";

type GetChargesParams = {
  startDate?: Date | null;
  endDate?: Date | null;
  chargeId?: number | null;
};

export const getCharges = async (
  params?: GetChargesParams,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({
    name: "StartDate",
    value: params?.startDate?.toISOString() ?? null,
  });
  parameters.push({
    name: "EndDate",
    value: params?.endDate?.toISOString() ?? null,
  });
  parameters.push({ name: "ChargeId", value: params?.chargeId ?? null });

  return await apiService.callV2("[SCRAP].[GetCharges]", parameters, signal);
};

export const getChargesForExport = async (startDate: Date, endDate: Date) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "StartDate", value: startDate.toISOString() });
  parameters.push({ name: "EndDate", value: endDate.toISOString() });

  return await apiService.callV2("[SCRAP].[GetChargesForExport]", parameters);
};

export const upsertCharge = async (charge: Charge) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push(
    { name: "ChargeId", value: charge.ChargeId ?? null },
    { name: "BucketId", value: charge.BucketId ?? null },
    { name: "RecipeId", value: charge.RecipeId ?? null },
    { name: "RecipeChargeId", value: charge.RecipeChargeId ?? null },
    { name: "OperatorId", value: charge.OperatorId ?? null },
    { name: "ManualTargetWeight", value: charge.ManualTargetWeight ?? null },
    { name: "Comments", value: charge.Comments ?? null },
    { name: "ChargeStatusCode", value: charge.ChargeStatusCode ?? null },
    {
      name: "ScheduledHeatName",
      value: charge.ScheduledHeatName?.toUpperCase() ?? null,
    },
  );

  return await apiService.callV2("[SCRAP].[UpsertCharge]", parameters);
};

export const createGroupNumber = async (
  recipeId: number,
  charge1: boolean,
  charge2: boolean,
  charge3: boolean,
  bucketId1: number,
  bucketId2: number,
  bucketId3: number,
  scheduledHeat: string | null,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push(
    { name: "RecipeId", value: recipeId },
    { name: "Charge1", value: charge1 },
    { name: "Charge2", value: charge2 },
    { name: "Charge3", value: charge3 },
    { name: "BucketId1", value: bucketId1 },
    { name: "BucketId2", value: bucketId2 },
    { name: "BucketId3", value: bucketId3 },
    {
      name: "ScheduledHeatName",
      value: scheduledHeat ? scheduledHeat?.toUpperCase() : null,
    },
  );
  return await apiService.callV2("[SCRAP].[CreateChargeGroup]", parameters);
};

export const upsertManualTargetWeight = async (
  chargeId: number,
  manualTargetWeight: number,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  parameters.push({ name: "ChargeId", value: chargeId });
  parameters.push({
    name: "ManualTargetWeight",
    value:
      manualTargetWeight !== undefined && manualTargetWeight !== null
        ? Number(manualTargetWeight)
        : null,
  });
  return await apiService.callV2(
    "[SCRAP].[UpsertManualTargetWeight]",
    parameters,
  );
};

export const upsertHeatWeightRequest = async (
  scheduledHeatName: string,
  requestedHeatWeight: number | null,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    {
      name: "ScheduledHeatName",
      value: scheduledHeatName.toUpperCase(),
    },
    {
      name: "RequestedHeatWeight",
      value: requestedHeatWeight,
    },
  ];

  return await apiService.callV2(
    "[SCRAP].[UpsertHeatWeightRequest]",
    parameters,
  );
};

export const deleteCharge = async (chargeId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "ChargeId", value: chargeId });

  return await apiService.callV2("[SCRAP].[DeleteCharge]", parameters);
};

export const getChargeLayers = async (
  chargeId: number,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "ChargeId", value: chargeId });

  return await apiService.callV2(
    "[SCRAP].[GetChargeLayers]",
    parameters,
    signal,
  );
};

export const getChargeLayerAudit = async (
  chargeId: number,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [{ name: "ChargeId", value: chargeId }];

  return await apiService.callV2(
    "[SCRAP].[GetChargeLayerAudit]",
    parameters,
    signal,
  );
};

type UpsertChargeLayerPayload = {
  ChargeLayerId?: number | null;
  ChargeId?: number | null;
  CommodityId?: number | null;
  Weight?: number | null;
  IsWeightManual?: boolean;
};

export const upsertChargeLayer = async (
  chargeLayer: UpsertChargeLayerPayload,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "ChargeLayerId", value: chargeLayer.ChargeLayerId ?? null },
    { name: "ChargeId", value: chargeLayer.ChargeId ?? null },
    { name: "CommodityId", value: chargeLayer.CommodityId ?? null },
    {
      name: "Weight",
      value:
        chargeLayer.Weight !== undefined && chargeLayer.Weight !== null
          ? Number(chargeLayer.Weight)
          : 0,
    },
    { name: "IsWeightManual", value: chargeLayer.IsWeightManual ?? true },
  ];

  return await apiService.callV2("[SCRAP].[UpsertChargeLayer]", parameters);
};

export const deleteChargeLayer = async (chargeLayerId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "ChargeLayerId", value: chargeLayerId });

  return await apiService.callV2("[SCRAP].[DeleteChargeLayer]", parameters);
};

export const reorderChargeLayer = async (
  chargeLayerId: number,
  newLayerNumber: number,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "ChargeLayerId", value: chargeLayerId },
    { name: "NewLayerNumber", value: newLayerNumber },
  ];

  return await apiService.callV2("[SCRAP].[ReorderChargeLayer]", parameters);
};

export const setCurrentChargeLayer = async (chargeLayerId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "ChargeLayerId", value: chargeLayerId });

  return await apiService.callV2("SCRAP.SetCurrentChargeLayer", parameters);
};

export const unsetCurrentChargeLayer = async (chargeLayerId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "ChargeLayerId", value: chargeLayerId });

  return await apiService.callV2("SCRAP.UnsetCurrentChargeLayer", parameters);
};

export const openCharge = async (chargeId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "ChargeId", value: chargeId });

  return await apiService.callV2("[SCRAP].[OpenCharge]", parameters);
};

export const closeCharge = async (chargeId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "ChargeId", value: chargeId });

  return await apiService.callV2("SCRAP.CloseCharge", parameters);
};

export const getBuckets = async (signal?: AbortSignal) => {
  const apiService = new MESApiService();
  return await apiService.callV2("[STEEL].[GetBuckets]", [], signal);
};

export const upsertBucket = async (bucket: number, capacity: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "BucketId", value: bucket });
  parameters.push({ name: "Capacity", value: capacity });

  return await apiService.callV2("[STEEL].[UpsertBucketCapacity]", parameters);
};

export const getOperators = async () => {
  const apiService = new MESApiService();
  return await apiService.callV2("[STEEL].[GetOperators]", []);
};

export const getSYOperators = async (signal?: AbortSignal) => {
  const apiService = new MESApiService();
  return await apiService.callV2("[STEEL].[GetSYOperators]", [], signal);
};

export const getNextChargeNumberByHeatName = async (heatName: string) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "HeatName", value: heatName?.toUpperCase() ?? null });

  return await apiService.callV2(
    "[SCRAP].[GetNextChargeNumberByHeatName]",
    parameters,
  );
};
